import React, { useState } from 'react';

import * as Yup from 'yup';
import { t } from 'i18next';
import { useFormik } from 'formik';
import moment from 'moment';

import styles from './ContactDetail.module.css';
import { EditIcon } from '../../../../assets/images/icons'; // Replace with correct import for the icon
import { useDispatch, useSelector } from 'react-redux';

import toast from 'react-hot-toast';
import Notice from '../Notice/Notice';
import Button from '../../Buttons/Button/Button';
import FormField from '../../FormUI/FormField/FormField';
import { Close } from '@mui/icons-material';
import { updateUser } from '../../../../services/userService';
import { fetchUserByToken, fetchUserByTokenWithoutLoading } from '../../../../app/features/userSlice';
import { formatDateForMySQL } from '../../../../utils/formatHelpers';
import FormSelectField from '../../FormUI/FormSelectField/FormSelectField';
import FormOptionField from '../../FormUI/FormOptionField/FormOptionField';
import FormDateField from '../../FormUI/FormDateField/FormDateField';
import { useGetCitiesOfState, useGetStates } from '../../../../utils/utilityHooks';
import BlockLoader from '../../Loaders/BlockLoader';

function ContactDetail({ jobseeker }) {
    const user = useSelector(state => state.user.user);
    const [formLoading, setFormLoading] = useState(false);
    const [editMode, setEditMode] = useState(true);
    const userToken = useSelector(state => state.user.token);
    const dispatch = useDispatch();
    const language = useSelector((state) => state.translation.language);
    const isRTL = language === 'ar';
    
    // Check if nationality and country are different to show visa status
    const shouldShowVisaStatus = (nationality, country) => {
        return nationality && country && nationality !== country;
    };
    
    const formik = useFormik({
        initialValues: {
            email: user?.email || '',
            permanent_address_line1: jobseeker?.permanent_address_line1 || '',
            permanent_address_line2: jobseeker?.permanent_address_line2 || '',
            permanent_country: jobseeker?.permanent_country || jobseeker?.nationality || '',
            permanent_state: jobseeker?.permanent_state || '',
            permanent_city: jobseeker?.permanent_city || '',
            permanent_postal_code: jobseeker?.permanent_postal_code || '',
            Address_Line_1: jobseeker?.Address_Line_1 || '',
            Address_Line_2: jobseeker?.Address_Line_2 || '',
            country: jobseeker?.country || '',
            state: jobseeker?.state || '',
            city: jobseeker?.city || '',
            postalCode: jobseeker?.postalCode || '',
            visa_status: jobseeker?.visa_status || '',
            visa_type: jobseeker?.visa_type || '',
            visa_expiry: jobseeker?.visa_expiry || '',
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            email: Yup.string().email(t('forms.create_account.invalid_email_address')).required(t('general.UI.required')),
            permanent_address_line1: Yup.string().required(t('general.UI.required')),
            permanent_country: Yup.string().required(t('general.UI.required')),
            permanent_postal_code: Yup.string().required(t('general.UI.required')),
            Address_Line_1: Yup.string().required(t('general.UI.required')),
            country: Yup.string().required(t('general.UI.required')),
            postalCode: Yup.string().required(t('general.UI.required')),
            visa_status: Yup.string().when(['permanent_country', 'country'], {
                is: (nationality, country) => shouldShowVisaStatus(nationality, country),
                then: schema => schema.required(t('general.UI.required')),
                otherwise: schema => schema.notRequired()
            }),
            visa_type: Yup.string().when('visa_status', {
                is: 'yes',
                then: schema => schema.required(t('general.UI.required')),
                otherwise: schema => schema.notRequired()
            }),
            visa_expiry: Yup.string().when('visa_status', {
                is: 'yes',
                then: schema => schema.required(t('general.UI.required')),
                otherwise: schema => schema.notRequired()
            }),
        }),
        onSubmit: (values) => {
            if (formLoading) {
                return;
            }
            setFormLoading(true);
            const formData = new FormData();
            formData.append('token', userToken);

            // Compare current values with initial values to find edited fields
            const editedFields = {};
            Object.keys(values).forEach(key => {
                // Skip email field if it hasn't been changed
                if (key === 'email' && values[key] === user.email) {
                    return;
                }

                if (key === 'visa_expiry' && values[key]) {
                    if (values[key] !== formik.initialValues[key]) {
                        editedFields[key] = formatDateForMySQL(values[key]);
                    }
                } else if (values[key] !== formik.initialValues[key]) {
                    editedFields[key] = values[key];
                }
            });

            // Only append edited fields to formData
            Object.keys(editedFields).forEach(key => {
                formData.append(key, editedFields[key]);
            });

            toast.promise(updateUser(formData), {
                loading: t('edit_profile.updating_profile'),
                success: <b>{t('edit_profile.profile_updated')}</b>,
                error: (err) => <b>{err.response.data.error}</b>,
            }).then(async () => {
                await dispatch(fetchUserByTokenWithoutLoading(userToken));
                await setEditMode(!editMode)
                await setFormLoading(false);
            })
                .catch((error) => {
                    setFormLoading(false);
                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error
                        });
                    }
                });
        },
    });

    const citiesOptions = useGetCitiesOfState(formik.values.state);
    const statesOptions = useGetStates(formik.values.country);
    const permanentCitiesOptions = useGetCitiesOfState(formik.values.permanent_state);
    const permanentStatesOptions = useGetStates(formik.values.permanent_country);

    // If user or jobseeker data is not available, show loader
    if (!user || !jobseeker) {
        return (
            <div className={styles.container}>
                <div className={styles.secondColumn}>
                    <BlockLoader height={800} />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* Second Column */}
            <div className={styles.secondColumn}>
                {!editMode &&
                    <Close
                        className={styles.mobileIcon}
                        style={{
                            cursor: "pointer"
                        }}
                        onClick={() => { setEditMode(!editMode) }}
                    />}
                <div className={styles.title}>
                    {t(`profileSidebar.tabs.contactDetails`)}
                </div>
                {editMode ?
                    <>
                        <div className={styles.aboutTitle}>{t('forms.login.email')}</div>
                        <div className={styles.description}>
                            {user?.email}
                        </div>
                    </>
                    :
                    <>
                        <div className={styles.aboutTitle}></div>
                        <div className={styles.editContainer}>
                            <FormField
                                name="email"
                                type="email"
                                label={t('edit_profile.job_seeker.email')}
                                hasBorder
                                icon={user?.email_verified && formik.values.email === user?.email && 'verified'}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                                error={formik.touched.email && formik.errors.email}
                            />
                            {!user?.email_verified && <Notice warning>{t('edit_profile.job_seeker.email_notice')}</Notice>}
                        </div>
                    </>
                }

                {/* Permanent Address Section */}
                <div className={styles.sectionTitle}>{t('contactDetails.permanentAddress')}</div>
                {editMode ? (
                    <>
                        {jobseeker?.permanent_address_line1 && <>
                            <div className={styles.aboutTitle}>{t('forms.welcome_address.AddressLine1')}</div>
                            <div className={styles.description}>
                                {jobseeker?.permanent_address_line1}
                            </div>
                        </>}
                        {jobseeker?.permanent_address_line2 && (
                            <>
                                <div className={styles.aboutTitle}>{t('forms.welcome_address.AddressLine2')}</div>
                                <div className={styles.description}>
                                    {jobseeker.permanent_address_line2}
                                </div>
                            </>
                        )}
                        {jobseeker?.permanent_country &&
                            <>
                                <div className={styles.aboutTitle}>{t('general.Country')}</div>
                                <div className={styles.description}>
                                    {t(`general.country.${jobseeker?.permanent_country}`)}
                                </div>
                            </>
                        }
                        {jobseeker?.permanent_state && <>
                            <div className={styles.aboutTitle}>{t('general.State')}</div>
                            <div className={styles.description}>
                                {t(`general.state.${jobseeker?.permanent_state}`)}
                            </div>

                        </>}
                        {jobseeker?.permanent_city &&
                            <>
                                <div className={styles.aboutTitle}>{t('general.City')}</div>
                                <div className={styles.description}>
                                    {t(`general.city.${jobseeker?.permanent_city}`)}
                                </div>
                            </>}
                        {jobseeker?.permanent_postal_code && <>
                            <div className={styles.aboutTitle}>{t('general.PostalCode')}</div>
                            <div className={styles.description}>
                                {jobseeker?.permanent_postal_code}
                            </div>
                        </>}
                    </>
                ) : (
                    <div className={styles.editContainer}>
                        <FormField
                            name="permanent_address_line1"
                            type="text"
                            label={t('forms.welcome_address.AddressLine1')}
                            hasBorder
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.permanent_address_line1}
                            error={formik.touched.permanent_address_line1 && formik.errors.permanent_address_line1}
                        />
                        <FormField
                            name="permanent_address_line2"
                            type="text"
                            label={t('forms.welcome_address.AddressLine2')}
                            hasBorder
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.permanent_address_line2}
                            error={formik.touched.permanent_address_line2 && formik.errors.permanent_address_line2}
                        />
                        <FormSelectField
                            name="permanent_country"
                            type="country"
                            label={t('general.Country')}
                            hasBorder
                            onFormikChange={formik.handleChange}
                            options={['QA', 'AE', 'SA', 'BH', 'KW', 'OM']}
                            value={formik.values.permanent_country}
                            error={formik.touched.permanent_country && formik.errors.permanent_country}
                        />
                        <FormSelectField
                            name="permanent_state"
                            type="default"
                            label={t('general.State')}
                            hasBorder
                            onFormikChange={formik.handleChange}
                            value={formik.values.permanent_state}
                            error={formik.touched.permanent_state && formik.errors.permanent_state}
                            options={permanentStatesOptions}
                        />
                        <FormSelectField
                            name="permanent_city"
                            type="default"
                            label={t('general.City')}
                            hasBorder
                            onFormikChange={formik.handleChange}
                            value={formik.values.permanent_city}
                            error={formik.touched.permanent_city && formik.errors.permanent_city}
                            options={permanentCitiesOptions}
                        />
                        <FormField
                            name="permanent_postal_code"
                            type="text"
                            label={t('general.PostalCode')}
                            hasBorder
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.permanent_postal_code}
                            error={formik.touched.permanent_postal_code && formik.errors.permanent_postal_code}
                        />
                    </div>
                )}

                {/* Current Resident Address Section */}
                <div className={styles.sectionTitle}>{t('contactDetails.currentResidentAddress')}</div>
                {editMode ? (
                    <>
                        {jobseeker?.country &&
                            <>
                                <div className={styles.aboutTitle}>{t('contactDetails.livingIn')}</div>
                                <div className={styles.description}>
                                    {t(`general.country.${jobseeker?.country}`)}
                                </div>
                            </>
                        }
                        <div className={styles.aboutTitle}>{t('forms.welcome_address.AddressLine1')}</div>
                        <div className={styles.description}>
                            {jobseeker?.Address_Line_1}
                        </div>
                        {jobseeker?.Address_Line_2 && (
                            <>
                                <div className={styles.aboutTitle}>{t('forms.welcome_address.AddressLine2')}</div>
                                <div className={styles.description}>
                                    {jobseeker.Address_Line_2}
                                </div>
                            </>
                        )}
                        {jobseeker?.state &&
                            <>
                                <div className={styles.aboutTitle}>{t('general.State')}</div>
                                <div className={styles.description}>
                                    {t(`general.state.${jobseeker?.state}`)}
                                </div>
                            </>}
                        {jobseeker?.city &&
                            <>
                                <div className={styles.aboutTitle}>{t('general.City')}</div>
                                <div className={styles.description}>
                                    {t(`general.city.${jobseeker?.city}`)}
                                </div>
                            </>}
                        {jobseeker?.postalCode &&
                            <>
                                <div className={styles.aboutTitle}>{t('general.PostalCode')}</div>
                                <div className={styles.description}>
                                    {jobseeker?.postalCode}
                                </div>
                            </>}
                        
                        {/* Display visa status information only if nationality and country are different */}
                        {shouldShowVisaStatus(jobseeker?.permanent_country, jobseeker?.country) && (
                            <>
                                {jobseeker?.visa_status &&
                                    <>
                                        <div className={styles.aboutTitle} dir={isRTL ? 'rtl' : 'ltr'}>{t('contactDetails.visaStatus')}</div>
                                        <div className={styles.description} dir={isRTL ? 'rtl' : 'ltr'}>
                                            {jobseeker?.visa_status === 'yes' ? t('contactDetails.iHaveValidVisa') : t('contactDetails.iDoNotHaveValidVisa')}
                                        </div>
                                    </>}
                                {jobseeker?.visa_status === 'yes' && (
                                    <>
                                        <div className={styles.aboutTitle} dir={isRTL ? 'rtl' : 'ltr'}>{t('contactDetails.visaType')}</div>
                                        <div className={styles.description} dir={isRTL ? 'rtl' : 'ltr'}>
                                            {t(`general.visaType.${jobseeker?.visa_type}`)}
                                        </div>
                                        <div className={styles.aboutTitle} dir={isRTL ? 'rtl' : 'ltr'}>{t('contactDetails.visaExpiry')}</div>
                                        <div className={styles.description} dir={isRTL ? 'rtl' : 'ltr'}>
                                            {jobseeker?.visa_expiry && moment(jobseeker?.visa_expiry).format('MMM DD, YYYY')}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <div className={styles.editContainer}>
                        <FormSelectField
                            name="country"
                            type="country"
                            label={t('contactDetails.livingIn')}
                            hasBorder
                            onFormikChange={formik.handleChange}
                            value={formik.values.country}
                            error={formik.touched.country && formik.errors.country}
                            options={['QA', 'AE', 'SA', 'BH', 'KW', 'OM']}
                        />
                        <FormField
                            name="Address_Line_1"
                            type="text"
                            label={t('forms.welcome_address.AddressLine1')}
                            hasBorder
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.Address_Line_1}
                            error={formik.touched.Address_Line_1 && formik.errors.Address_Line_1}
                        />
                        <FormField
                            name="Address_Line_2"
                            type="text"
                            label={t('forms.welcome_address.AddressLine2')}
                            hasBorder
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.Address_Line_2}
                            error={formik.touched.Address_Line_2 && formik.errors.Address_Line_2}
                        />
                        <FormSelectField
                            name="state"
                            type="default"
                            label={t('general.State')}
                            hasBorder
                            onFormikChange={formik.handleChange}
                            value={formik.values.state}
                            error={formik.touched.state && formik.errors.state}
                            options={statesOptions}
                        />
                        <FormSelectField
                            name="city"
                            type="default"
                            label={t('general.City')}
                            hasBorder
                            onFormikChange={formik.handleChange}
                            value={formik.values.city}
                            error={formik.touched.city && formik.errors.city}
                            options={citiesOptions}
                        />
                        <FormField
                            name="postalCode"
                            type="text"
                            label={t('general.PostalCode')}
                            hasBorder
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.postalCode}
                            error={formik.touched.postalCode && formik.errors.postalCode}
                        />
                        
                        {/* Show visa status section only if nationality and resident country are different */}
                        {shouldShowVisaStatus(formik.values.permanent_country, formik.values.country) && (
                            <>
                                <div className={styles.visaStatusTitle} dir={isRTL ? 'rtl' : 'ltr'}>
                                    {t('contactDetails.visaInformationTitle')}
                                </div>
                                <div className={styles.visaStatusDescription} dir={isRTL ? 'rtl' : 'ltr'}>
                                    {t('contactDetails.visaInformationDescription')}
                                </div>
                                <FormOptionField
                                    name="visa_status"
                                    label={t('contactDetails.doYouHaveValidVisa')}
                                    type="radio"
                                    dark
                                    options={[
                                        { value: 'yes', label: t('contactDetails.iHaveValidVisa') },
                                        { value: 'no', label: t('contactDetails.iDoNotHaveValidVisa') }
                                    ]}
                                    onChange={formik.handleChange}
                                    value={formik.values.visa_status}
                                    error={formik.touched.visa_status && formik.errors.visa_status}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                {formik.values.visa_status === 'yes' && (
                                    <>
                                        <FormSelectField
                                            name="visa_type"
                                            type="default"
                                            label={t('contactDetails.visaType')}
                                            hasBorder
                                            options={[
                                                { value: 'visit', label: t('contactDetails.visitVisa') },
                                                { value: 'work', label: t('contactDetails.workVisa') },
                                                { value: 'resident', label: t('contactDetails.residentVisa') }
                                            ]}
                                            onFormikChange={formik.handleChange}
                                            value={formik.values.visa_type}
                                            error={formik.touched.visa_type && formik.errors.visa_type}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <FormDateField
                                            name="visa_expiry"
                                            label={t('contactDetails.visaExpiry')}
                                            hasBorder
                                            value={formik.values.visa_expiry}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.visa_expiry && formik.errors.visa_expiry}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Save Button */}
                {!editMode && (
                    <div className={styles.saveButtonContainer}>
                        <Button onClick={formik.submitForm} className={styles.saveButton}>
                            {formLoading ? t('general.UI.loading') : t('general.UI.save')}
                        </Button>
                    </div>
                )}
            </div>
            <div className={styles.thirdColumn}>
                {!editMode ? (
                    <Close
                        className={styles.crossIcon}
                        style={{ cursor: "pointer" }}
                        onClick={() => setEditMode(!editMode)}
                    />
                ) : (
                    <img
                        src={EditIcon}
                        onClick={() => setEditMode(!editMode)}
                        alt={t('personalInformation.edit')}
                        className={styles.editIcon}
                    />
                )}
            </div>
        </div>
    );
}

export default ContactDetail;
